import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class WebSocketJs {
  url = '';
  onCreate: ((data: any, notification: any) => void) | undefined;
  onUpdate: ((data: any, notification: any) => void) | undefined;
  onOpen: (() => void) | undefined;
  onData: ((data: any, notification: any) => void) | undefined;
  ws: WebSocket | undefined;
  topics: string[] = [];
  callbacks = new Map<string, { onCreate?: (data: any, notification: any) => void; onUpdate?: (data: any, notification: any) => void; onData?: (data: any, notification: any) => void }>();

  private pingTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private pongTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private readonly pingMsg = { action: 'heartbeat', payload: { message: { text: 'ping' } } };
  private readonly pongMsg = { action: 'heartbeat', payload: { message: { text: 'pong' } } };
  private readonly pongTimeout = 10000;
  private readonly pingTimeout = 15000;

  ref(topic: string, _calledBy: string, onCreate?: (data: any, notification: any) => void, onUpdate?: (data: any, notification: any) => void, onData?: (data: any, notification: any) => void) {
    if (!this.callbacks) {
      return;
    }

    this.callbacks.set(topic, { onCreate, onUpdate, onData });

    if (this.ws && this.ws.readyState === 1) {
      this.subscribe(topic);
    } else if (this.ws) {
      const that = this;
      this.ws.addEventListener('open', function () {
        that.subscribe(topic);
      });
    }

    if (this.topics.indexOf(topic) === -1) {
      this.topics.push(topic);
    }
  }

  subscribe(topic: string) {
    if (this.topics.indexOf(topic) === -1) {
      this.topics.push(topic);
    }

    const message = {
      action: 'subscribe',
      payload: {
        topic,
        message: undefined,
        method: undefined,
      },
    };

    this.send(JSON.stringify(message), `SUBSCRIBE to ${topic}`);
  }

  unsubscribe(topic: string) {
    const index = this.topics.indexOf(topic);
    if (index > -1) {
      this.topics.splice(index, 1);
    }

    if (this.callbacks.size > 0) {
      this.callbacks.delete(topic);
    }

    const message = {
      action: 'unsubscribe',
      payload: {
        topic,
        message: undefined,
        method: undefined,
      },
    };

    if (this.ws && this.ws.readyState === 1) {
      this.send(JSON.stringify(message), `UNSUBSCRIBE from ${topic}`);
    }
  }

  send(initialMessage: string, _callingMethod?: string) {
    this.ws?.send(initialMessage);
  }

  close() {
    this.topics = [];
    this.callbacks = new Map();

    if (this.ws) {
      this.ws.onclose = function () {};
      this.ws.close();
      this.ws = undefined;
    }
    this.heartReset();
  }

  resubscribe() {
    if (this.topics.length > 0) {
      this.topics.forEach((topic) => this.subscribe(topic));
    }
  }

  heartCheck() {
    this.heartReset();
    this.heartStart();
  }

  heartStart() {
    this.pingTimeoutId = setTimeout(() => {
      if (this.ws && this.ws.readyState === 1) {
        this.send(JSON.stringify(this.pingMsg), 'HEART-START');
      }

      this.pongTimeoutId = setTimeout(() => {
        this.ws?.close();
      }, this.pongTimeout);
    }, this.pingTimeout);
  }

  heartReset() {
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
    }
    if (this.pongTimeoutId) {
      clearTimeout(this.pongTimeoutId);
    }
  }

  init(
    url: string,
    onCreate?: (data: any, notification: any) => void,
    onUpdate?: (data: any, notification: any) => void,
    onData?: (data: any, notification: any) => void,
    onOpen?: () => void,
    onOpenCallback?: () => void,
    _topics: string[] = [],
    _callbacks = new Map<string, { onCreate?: (data: any, notification: any) => void; onUpdate?: (data: any, notification: any) => void; onData?: (data: any, notification: any) => void }>(),
  ) {
    this.url = url;
    this.onCreate = onCreate;
    this.onUpdate = onUpdate;
    this.onData = onData;
    this.onOpen = onOpen;
    this.topics = _topics;
    this.callbacks = _callbacks;

    const that = this;
    return new Promise<void>((resolve) => {
      that.ws = new WebSocket(that.url);

      that.ws.onopen = function () {
        that.heartCheck();
        onOpenCallback?.();
        that.onOpen?.();
      };

      that.ws.onclose = function () {
        setTimeout(function () {
          that.init(url, onCreate, onUpdate, onData, onOpen, function () {
            onOpenCallback?.();
            that.resubscribe();
          }, that.topics, that.callbacks);
        }, 3000);
      };

      that.ws.onerror = function () {};

      that.ws.onmessage = function (message) {
        console.log(1111111111111111111111, message);
        let json: any;
        try {
          json = JSON.parse(message.data);
        } catch {
          return;
        }

        that.heartCheck();

        if (json.action === 'heartbeat') {
          if (json.payload?.message?.text === 'ping') {
            that.send(JSON.stringify(that.pongMsg), 'ON-MESSAGE');
          }
          return;
        }

        const object = { event: json.payload, data: json };
        if (that.onData) {
          that.onData(json.payload.message, object);
        }

        const callbackObj = that.callbacks.get(object.event.topic);
        if (callbackObj?.onData) {
          callbackObj.onData(json.payload.message, object);
        }

        if (json?.payload?.message && that.isArray(json.payload.message)) {
          json.payload.message.forEach((element: any) => {
            const insUp = json.payload.method;
            const itemObject = { event: json.payload, data: element };
            const itemCallback = that.callbacks.get(itemObject.event.topic);

            if (insUp === 'CREATE') {
              that.onCreate?.(element, itemObject);
              itemCallback?.onCreate?.(element, itemObject);
            }

            if (insUp === 'UPDATE') {
              that.onUpdate?.(element, itemObject);
              itemCallback?.onUpdate?.(element, itemObject);
            }
            resolve();
          });
        } else {
          const insUp = json.payload.method;
          const itemObject = { event: json.payload, data: json };
          const itemCallback = that.callbacks.get(itemObject.event.topic);

          if (insUp === 'CREATE') {
            that.onCreate?.(json.payload.message, itemObject);
            itemCallback?.onCreate?.(json.payload.message, itemObject);
          }

          if (insUp === 'UPDATE') {
            that.onUpdate?.(json.payload.message, itemObject);
            itemCallback?.onUpdate?.(json.payload.message, itemObject);
          }

          resolve();
        }
      };
    });
  }

  private isArray(value: unknown): value is unknown[] {
    return Object.prototype.toString.call(value) === '[object Array]';
  }
}
